import { useState, useEffect, useRef, useCallback } from "react";
import AddUserDrawer from "../components/AddUserDrawer";
import ChangePasswordModal from "../components/ChangePasswordModal";
import InviteUserModalV2 from "../components/InviteUserModalV2";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../helpers/axios";
import userService from "../services/userService";
import {
  MagnifyingGlassIcon,
  KeyIcon,
  ChevronDownIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import { useDashboardUI } from "../context/GlobalContext";
import { validateUserRole, formatRole, decodeAccessToken } from "../helpers/helpers";
import { userRoles } from "../helpers/constant";

// Shortens long text to 20 characters and adds "…" so the table stays tidy.
// The full value is shown in a tooltip (see the `title` attribute below).
const truncate = (text) =>
  text && text.length > 20 ? text.slice(0, 20) + "…" : text;

// Soft tinted colours for the initials avatars; each user always gets the same
// one (picked from their name), so the list looks varied but stable.
const AVATAR_COLORS = [
  "bg-[#F3EAFE] text-[#7C3AED]",
  "bg-[#E8EAFB] text-[#3F4BB8]",
  "bg-[#E7F7EE] text-[#12945A]",
  "bg-[#FEF3C7] text-[#B45309]",
  "bg-[#FCE7F3] text-[#BE185D]",
  "bg-[#E0F2FE] text-[#0369A1]",
  "bg-[#F1F2F7] text-[#4B5266]",
];
const avatarColorClass = (key = "") => {
  let hash = 0;
  for (const ch of String(key)) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// Role pill colours (Basic = neutral gray, Admin = indigo, Super Admin = purple).
const ROLE_PILL_CLASS = {
  superadmin: "bg-[#F3EAFE] text-[#7C3AED]",
  admin: "bg-[#E8EAFB] text-[#3F4BB8]",
};
const DEFAULT_ROLE_PILL_CLASS = "bg-[#F1F2F7] text-[#4B5266]";

const UserManagementV2 = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [toggles, setToggles] = useState({});

  // Tracks which user's invite is currently being re-sent, so we can disable
  // just that row's "Resend invite" button while the request is in flight.
  const [resendingUserId, setResendingUserId] = useState(null);

  // Department filter. "all" means no filter (the default selection).
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const { selectedFacilityFilter, setPageLoader } = useDashboardUI();
  const navigate = useNavigate();

  // Facility used for the department dropdown and Add User.
  //  - Superadmin: the facility chosen in the dropdown, or their own if none is selected.
  //  - Admin: always their own facility (the backend enforces this too).
  const currentUser = decodeAccessToken();
  const facilityId = validateUserRole(userRoles.SUPERADMIN)
    ? selectedFacilityFilter?.facility_number ?? currentUser?.facility_id
    : currentUser?.facility_id;

  // Pagination state (matching PromptManagement)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // A department belongs to one facility, so when the facility changes the
  // previously selected department no longer applies. We reset the filter back
  // to "all" (and jump to page 1) the moment the facility changes. Adjusting
  // state during render is React's recommended way to reset state when a value
  // it depends on changes — it runs before the fetch effect, so the user list
  // is loaded with "all" straight away instead of the stale department.
  const [prevFacilityId, setPrevFacilityId] = useState(facilityId);
  if (facilityId !== prevFacilityId) {
    setPrevFacilityId(facilityId);
    setSelectedDepartment("all");
    setPage(1);
  }

  // Action menu state
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const actionMenuRef = useRef(null);

  // Change password modal state
  const [changePasswordUser, setChangePasswordUser] = useState(null);

  const canChangePassword =
    validateUserRole(userRoles.SUPERADMIN) ||
    (process.env.REACT_APP_ADMIN_CHANGE_PASSWORD === "true" &&
      validateUserRole(userRoles.ADMIN));

  // Close action menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setOpenActionMenu(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = async (userId, currentStatus) => {
    try {
      // Optimistically update the UI
      setToggles((prev) => ({ ...prev, [userId]: !currentStatus }));

      // Call API to update user active status
      const response = await api.patch('/users/update-status', {
        user_id: userId,
        is_active: !currentStatus
      });

      // Refresh the listing after successful update (200 response)
      if (response.status === 200) {
        await fetchUsers();
      }
    } catch (err) {
      console.error("Failed to update user status:", err);
      // Revert the toggle on error
      setToggles((prev) => ({ ...prev, [userId]: currentStatus }));
      setError("Failed to update user status. Please try again.");
    }
  };

  const fetchUsers = async () => {
    const adminEmail = localStorage.getItem("admin_email");
    if (!adminEmail) {
      setError("Missing admin email in localStorage.");
      setLoading(false);
      return;
    }
    try {
      setPageLoader(true);

      // Build the query string from the active filters.
      const queryParams = new URLSearchParams();
      if (selectedFacilityFilter && selectedFacilityFilter.facility_number) {
        queryParams.append("facility_id", selectedFacilityFilter.facility_number);
      }
      if (selectedDepartment !== "all") {
        queryParams.append("org_department", selectedDepartment);
      }
      const queryString = queryParams.toString();
      const apiPath = queryString ? `/users/?${queryString}` : `/users/`;

      const res = await api.get(apiPath);

      // Apply search filter client-side
      let filteredUsers = res.data;
      if (debouncedSearch) {
        filteredUsers = res.data.filter((user) =>
          user.email.toLowerCase().includes(debouncedSearch.toLowerCase())
        );
      }

      // Calculate pagination
      const totalUsers = filteredUsers.length;
      const calculatedTotalPages = Math.ceil(totalUsers / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

      setUsers(paginatedUsers);
      setTotal(totalUsers);
      setTotalPages(calculatedTotalPages || 1);

      // Initialize toggle states based on is_active field
      const initialToggles = {};
      res.data.forEach(user => {
        initialToggles[user.id] = user.is_active;
      });
      setToggles(initialToggles);

      setError(null);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setError("Failed to load users.");
    } finally {
      setLoading(false);
      setPageLoader(false);
    }
  };

  // Loads the department options for the filter dropdown. Defined with
  // useCallback so it can be reused both on facility change and after a user is
  // added (to pick up any newly created department).
  const loadDepartments = useCallback(async () => {
    try {
      const data = await userService.getDepartments(facilityId);
      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch departments:", err);
    }
  }, [facilityId]);

  // Refetch the department options whenever the facility changes.
  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  // After a user is added, refresh the listing and the department dropdown.
  // The current department selection stays as-is (we don't reset it).
  const handleUserAdded = () => {
    fetchUsers();
    loadDepartments();
  };

  // Re-sends the invite to a user who hasn't finished setting up their account.
  const handleResendInvite = async (user) => {
    try {
      setResendingUserId(user.id);
      await userService.resendInvite(user.email);
      toast.success("Invite re-sent successfully");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to resend invite. Please try again."
      );
    } finally {
      setResendingUserId(null);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  // Fetch users when filters change
  useEffect(() => {
    fetchUsers();
  }, [page, limit, debouncedSearch, selectedFacilityFilter, selectedDepartment]);

  return (
    <div className="w-full flex flex-col h-full lg:h-[calc(100vh-100px)] pt-4 gap-3">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
        {/* Search + Department filter (top-left) */}
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-auto">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users by email"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              autoComplete="off"
              readOnly
              onFocus={(e) => e.currentTarget.removeAttribute("readonly")}
              className="w-full sm:w-80 h-10 sm:h-11 rounded-xl border border-[#ECEEF4] bg-white pl-11 pr-4 sm:pr-5 text-sm text-gray-700 placeholder:text-[#9AA1B2] transition-colors hover:border-[#D9DCE6] focus:outline-none focus:border-[#8B5CF6]/50 focus:ring-2 focus:ring-[#8B5CF6]/10"
            />
          </div>

          <div className="relative min-w-0 flex-1 sm:flex-none">
            <select
              value={selectedDepartment}
              onChange={(e) => {
                setPage(1);
                setSelectedDepartment(e.target.value);
              }}
              className="w-full sm:w-52 h-11 appearance-none truncate rounded-xl border border-[#ECEEF4] bg-white pl-4 pr-11 text-sm font-medium text-[#14122B] cursor-pointer transition-colors hover:border-[#D9DCE6] focus:outline-none focus:border-[#8B5CF6]/50 focus:ring-2 focus:ring-[#8B5CF6]/10"
            >
              <option value="all">All Departments</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
            {/* Custom chevron (the native arrow is hidden via appearance-none) */}
            <ChevronDownIcon className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
        </div>

        {/* Add User + Invite User buttons (top-right) */}
        {validateUserRole(userRoles.SUPERADMIN) && (
          <div className="flex w-full items-center gap-2 sm:w-auto sm:gap-3 [&>button]:flex-1 [&>button]:justify-center [&>button]:whitespace-nowrap sm:[&>button]:flex-none">
            <button
              onClick={() => setInviteOpen(true)}
              className="flex items-center gap-2 rounded-xl border border-[#2B3674] bg-white px-4 sm:px-6 h-11 text-sm font-medium text-[#2B3674] hover:bg-[#2B3674]/5 transition"
            >
              Invite User
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="flex items-center gap-2 rounded-xl bg-[#2B3674] px-4 sm:px-6 h-11 text-sm font-medium text-white hover:bg-[#232d61] transition"
            >
              Add User <span className="text-lg">+</span>
            </button>
          </div>
        )}
      </div>

      {/* Table card (same look as Feedback Items): sticky header, one scroll area */}
      <div className="flex-1 min-h-0 bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
        <div className="table-scroll h-full overflow-auto">
        <table className="w-full min-w-[1200px] xl:min-w-[960px] table-fixed border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              {[
                { label: "Name", width: "18%", align: "left" },
                { label: "Email", width: "22%", align: "left" },
                { label: "Role", width: "15%", align: "left" },
                { label: "Org Department", width: "14%", align: "left" },
                { label: "Last Login", width: "15%", align: "left" },
                { label: "Active", width: "8%", align: "right" },
                { label: "Actions", width: "8%", align: "right" },
              ].map(({ label, width, align }) => (
                <th
                  key={label}
                  style={{ position: "sticky", top: 0, background: "#F5F2FF", zIndex: 20, width, textAlign: align }}
                  className={`py-3.5 align-middle text-xs font-semibold text-gray-500 uppercase tracking-wide ${align === "right" ? "pr-6" : "pl-6"}`}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-10 text-center text-sm text-gray-500"
                >
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2B3674] mr-3"></div>
                    Loading users...
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-10 text-center text-sm text-red-600"
                >
                  {error}
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-10 text-center text-sm text-gray-500"
                >
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user, index) => (
                <tr
                  key={user.id}
                  onClick={() => navigate(`/users/${user.id}`, { state: { user } })}
                  className="cursor-pointer border-b border-gray-100 last:border-0 transition-colors hover:bg-gray-50"
                >
                  <td className="pl-6 pr-3 py-3 align-middle">
                    <div className="flex items-center gap-2.5">
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center text-xs font-semibold shrink-0 ${avatarColorClass(user.display_name || user.email)}`}>
                        {(user.display_name || user.email || "U")
                          .split(" ")
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((n) => n[0])
                          .join("")
                          .toUpperCase()}
                      </div>
                      <span className="min-w-0 truncate text-sm font-semibold text-gray-900" title={user.display_name}>
                        {user.display_name}
                      </span>
                    </div>
                  </td>

                  <td className="pl-6 pr-3 py-3 align-middle text-sm text-gray-600 truncate" title={user.email}>
                    {user.email}
                  </td>

                  <td
                    className="pl-6 pr-3 py-3 align-middle"
                    title={formatRole(user.user_role)}
                  >
                    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${ROLE_PILL_CLASS[user.user_role] || DEFAULT_ROLE_PILL_CLASS}`}>
                      {truncate(formatRole(user.user_role))}
                    </span>
                  </td>

                  <td
                    className="pl-6 pr-3 py-3 align-middle text-sm text-gray-600"
                    title={user.org_department || ""}
                  >
                    {truncate(user.org_department) || "-"}
                  </td>

                  {/* <td className="w-[13%] px-6 py-4 text-gray-500">
                    {new Date(user.created_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </td> */}

                  <td className="pl-6 pr-3 py-3 align-middle text-sm text-gray-600 whitespace-nowrap">
                    {user.last_login_details
                      ? new Date(user.last_login_details + 'Z').toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "-"}
                  </td>

                  <td className="relative pr-6 py-3 align-middle text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggle(user.id, toggles[user.id]);
                        }}
                        className={`
                          relative inline-flex items-center h-6 w-11 flex-shrink-0 px-1
                          cursor-pointer rounded-full transition-colors duration-200
                          ${toggles[user.id] ? "bg-green-500" : "bg-red-500"}
                        `}
                      >
                        <span
                          className={`
                            inline-block h-4 w-4 transform rounded-full bg-white shadow
                            transition-transform duration-200
                            ${
                              toggles[user.id]
                                ? "translate-x-5"
                                : "translate-x-0"
                            }
                          `}
                        />
                      </button>
                    </div>
                  </td>

                  <td className="relative pr-6 py-3 align-middle text-right">
                    {/* Setup not finished (setup_status false) → only "Resend
                        invite". Setup finished (true) → only "Reset password"
                        (when allowed). The menu hides when neither applies. */}
                    {(!user.setup_status || (user.setup_status && canChangePassword)) && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenActionMenu(
                              openActionMenu === user.id ? null : user.id
                            );
                          }}
                          title="Actions"
                          className="rounded-full p-2 hover:bg-gray-100 transition"
                        >
                          <EllipsisVerticalIcon className="w-5 h-5 text-gray-600" />
                        </button>

                        {openActionMenu === user.id && (
                          <div
                            ref={actionMenuRef}
                            className="absolute right-8 top-12 z-20 w-48 overflow-hidden rounded-xl border bg-white text-left shadow-lg"
                          >
                            {!user.setup_status && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenActionMenu(null);
                                  handleResendInvite(user);
                                }}
                                disabled={resendingUserId === user.id}
                                className="flex w-full items-center gap-2.5 px-4 py-3 text-sm text-[#2B3674] hover:bg-gray-50 disabled:opacity-50"
                              >
                                <EnvelopeIcon className="w-4 h-4" />
                                {resendingUserId === user.id
                                  ? "Sending..."
                                  : "Resend invite"}
                              </button>
                            )}

                            {user.setup_status && canChangePassword && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenActionMenu(null);
                                  setChangePasswordUser(user);
                                }}
                                className="flex w-full items-center gap-2.5 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <KeyIcon className="w-4 h-4 text-purple-600" />
                                Reset password
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination (same layout as Feedback Items / Prompt Management) */}
      <div className="mt-auto pt-1 pb-1 flex flex-nowrap items-center justify-between gap-2 whitespace-nowrap text-sm flex-shrink-0">
        <span>Total {total}</span>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            Rows per page
            <select
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
              className="rounded-md border px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl disabled:opacity-50"
            >
              ‹
            </button>

            <span>
              {page} / {totalPages}
            </span>

            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl disabled:opacity-50"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <AddUserDrawer
        open={modalOpen}
        facilityId={facilityId}
        onClose={() => setModalOpen(false)}
        onSuccess={handleUserAdded}
      />

      {inviteOpen && (
        <InviteUserModalV2
          facilityId={facilityId}
          onClose={() => setInviteOpen(false)}
          onSuccess={fetchUsers}
        />
      )}

      {changePasswordUser && (
        <ChangePasswordModal
          user={changePasswordUser}
          onClose={() => setChangePasswordUser(null)}
          onSuccess={fetchUsers}
        />
      )}
    </div>
  );
};

export default UserManagementV2;
